// DTC <-> byte encoding and code description. Shared by the DTC Decoder and the UDS
// Diagnostic Session Simulator (lib/uds/dtc.js re-exports the encode/decode helpers).
//
// Two-byte packing (SAE J2012 / ISO 15031-6): byte 0 bits 7-6 select the P/C/B/U
// letter, bits 5-4 the first digit (0-3), bits 3-0 the second character; byte 1 holds
// the last two hex characters. A UDS DTC adds a 3rd byte, which under the
// SAE_J2012-DA_DTCFormat_04 convention is the Failure Type Byte (FTB).
import { DTC_LETTERS, DTC_DIGIT1, DTC_SUBGROUPS, FTB_CATEGORIES, FTB_NAMES, hex2 } from './constants';
import { lookupDtc } from './dtcDatabase';

const LETTERS = ['P', 'C', 'B', 'U'];
const LETTER_INDEX = { P: 0, C: 1, B: 2, U: 3 };

export function dtcBytesToCode([b0, b1]) {
  const letter = LETTERS[(b0 >> 6) & 0x03];
  const d1 = (b0 >> 4) & 0x03;
  const d2 = b0 & 0x0f;
  const d3 = (b1 >> 4) & 0x0f;
  const d4 = b1 & 0x0f;
  const hex = (n) => n.toString(16).toUpperCase();
  return `${letter}${hex(d1)}${hex(d2)}${hex(d3)}${hex(d4)}`;
}

export function dtcCodeToBytes(code, failureByte = 0x00) {
  const m = /^([PCBU])([0-3])([0-9A-Fa-f])([0-9A-Fa-f])([0-9A-Fa-f])$/.exec(code.trim());
  if (!m) throw new Error(`"${code}" is not a valid DTC code (expected e.g. P0A1F).`);
  const [, letter, d1, d2, d3, d4] = m;
  const b0 = (LETTER_INDEX[letter] << 6) | (parseInt(d1, 16) << 4) | parseInt(d2, 16);
  const b1 = (parseInt(d3, 16) << 4) | parseInt(d4, 16);
  return [b0, b1, failureByte & 0xff];
}

// Matches "P0301", "p0a1f-1c", "P0A1F:1C", "P0A1F 1C", "P0A1F1C". Returns null if the
// string is not DTC-shaped at all; { error } if it is DTC-shaped but invalid.
const DTC_RE = /^([PCBU])([0-9])([0-9A-F]{3})(?:\s*[-:\s]\s*([0-9A-F]{2})|([0-9A-F]{2}))?$/i;
export function parseDtcString(raw) {
  const s = raw.trim().replace(/^0x/i, '');
  const m = DTC_RE.exec(s);
  if (!m) return null;
  const [, letter, d1, rest, ftbSep, ftbJoined] = m;
  if (Number(d1) > 3) {
    return { error: `"${s.toUpperCase()}" is not a valid DTC — the digit after the letter must be 0-3 (it is only 2 bits wide in the encoded bytes), got ${d1}.` };
  }
  const code = `${letter}${d1}${rest}`.toUpperCase();
  const ftbHex = ftbSep || ftbJoined;
  return { code, ftb: ftbHex !== undefined ? parseInt(ftbHex, 16) : null };
}

export function describeDigit1(letter, d1, d2) {
  const entry = DTC_DIGIT1[letter][d1];
  if (entry.scope !== 'split') return { ...entry };
  // P3: 2nd character 0-3 -> manufacturer, 4-F -> ISO/SAE.
  const manufacturer = parseInt(d2, 16) <= 3;
  return manufacturer
    ? { scope: 'manufacturer', label: 'Manufacturer controlled (P3000–P33FF)' }
    : { scope: 'generic', label: 'ISO/SAE controlled / reserved (P3400–P3FFF)' };
}

export function describeSubgroup(letter, d1, d2) {
  const table = DTC_SUBGROUPS[`${letter}${d1}`];
  if (table && table[d2]) return table[d2];
  if (letter === 'B' || letter === 'C') return null; // J2012 defines no standard sub-groups for body/chassis
  return null;
}

// Full description of a 2-byte code. `description` is only ever taken from the
// curated database — for anything else `description` is null and `unknownReason`
// explains why, so the UI never has to invent text.
export function describeDtc(code) {
  const letter = code[0];
  const d1 = code[1];
  const d2 = code[2];
  const letterInfo = DTC_LETTERS[letter];
  const digit1 = describeDigit1(letter, d1, d2);
  const subgroup = describeSubgroup(letter, d1, d2);
  const entry = lookupDtc(code);
  let unknownReason = null;
  if (!entry) {
    if (digit1.scope === 'manufacturer') {
      unknownReason = `${code} is a manufacturer-controlled code. Its meaning is defined by the vehicle or ECU manufacturer, so this tool does not state one — check the OEM's service information.`;
    } else if (digit1.scope === 'reserved') {
      unknownReason = `${code} is in a range reserved by SAE J2012. No generic meaning is defined for it.`;
    } else {
      unknownReason = `${code} is a generic (ISO/SAE-controlled) code, but it is not in this tool's curated list of about 170 common codes. Its official wording is in SAE J2012-DA, which is copyrighted and not reproduced here.`;
    }
  }
  return {
    code,
    letter,
    letterInfo,
    digit1,
    subgroup,
    entry,
    unknownReason,
    characters: [
      { char: letter, role: 'System', meaning: `${letterInfo.name} — ${letterInfo.covers}` },
      { char: d1, role: 'Code type', meaning: digit1.label },
      { char: d2, role: 'Sub-group', meaning: subgroup || (letter === 'B' || letter === 'C' ? 'No standard sub-group — J2012 does not group body/chassis codes' : 'No standard sub-group assigned for this range') },
      { char: code.slice(3), role: 'Fault number', meaning: 'Identifies the specific fault within the sub-group' },
    ],
  };
}

// Bit-level map of the 2 code bytes, for the visual byte -> bits -> characters diagram.
export function dtcBitFields(b0, b1) {
  const bits = (byte) => byte.toString(2).padStart(8, '0').split('');
  const code = dtcBytesToCode([b0, b1]);
  return {
    code,
    bytes: [b0, b1],
    bits: [...bits(b0), ...bits(b1)],
    groups: [
      { from: 0, to: 1, char: code[0], label: 'Letter' },
      { from: 2, to: 3, char: code[1], label: 'Digit 1' },
      { from: 4, to: 7, char: code[2], label: 'Char 3' },
      { from: 8, to: 11, char: code[3], label: 'Char 4' },
      { from: 12, to: 15, char: code[4], label: 'Char 5' },
    ],
  };
}

export function decodeFtb(ftb) {
  const nibble = ftb >> 4;
  let category;
  if (FTB_CATEGORIES[nibble]) category = FTB_CATEGORIES[nibble].name;
  else if (ftb >= 0xf0) category = 'Manufacturer / supplier specific';
  else category = 'ISO/SAE reserved';
  const name = FTB_NAMES[ftb] || null;
  let note = null;
  if (!name) {
    if (ftb >= 0xf0) note = 'FTBs 0xF0-0xFF are for vehicle-manufacturer / system-supplier use — check the OEM specification.';
    else if (ftb >= 0xa0) note = 'This FTB is in an ISO/SAE reserved range.';
    else note = 'Only the category is shown. The exact sub-type name for this FTB comes from SAE J2012-DA, and this tool only names sub-types it could verify from two independent public sources.';
  }
  return { ftb, hex: hex2(ftb), category, categoryDetail: FTB_CATEGORIES[nibble]?.detail || null, name, note };
}
